'use client'

import type { VariantProps } from 'class-variance-authority'
import { useRef } from 'react'
import { useTrackElementImpression, useTrackElementInteracted } from '@nwwp/segment-analytics'
import type { buttonVariants } from '@nwwp/ui-shadcn'
import { Button, cn } from '@nwwp/ui-shadcn'

type Props = {
  href?: string
  target?: string
  children?: React.ReactNode
  className?: string
  disabled?: boolean
  'aria-label'?: string
  id?: string
  type?: 'button' | 'submit' | 'reset'

  /**
   * The name of the event to track.
   *
   * @default "Element Interacted"
   */
  segmentEventName?: string

  /**
   * The name of the element to track.
   *
   * @default "ButtonCTA"
   */
  segmentElement?: string

  /**
   * When true, scrolls to top of page on click.
   */
  scrollToTop?: boolean

  onClick?: React.MouseEventHandler<HTMLButtonElement>
} & VariantProps<typeof buttonVariants>

export const ButtonCTA = ({
  href,
  target,
  children,
  segmentEventName,
  segmentElement,
  scrollToTop,
  onClick,
  className,
  variant,
  size,
  type,
  ...props
}: Props) => {
  const trackElementInteracted = useTrackElementInteracted()
  const buttonRef = useRef<HTMLButtonElement>(null)

  useTrackElementImpression(buttonRef, {
    element: segmentElement ?? 'ButtonCTA',
  })

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    await trackElementInteracted({
      element: segmentElement ?? 'ButtonCTA',
      ...(segmentEventName ? { eventName: segmentEventName } : {}),
      href,
    })

    if (href) {
      if (target === '_blank') {
        const a = document.createElement('a')
        a.href = href
        a.target = '_blank'
        a.rel = 'noopener noreferrer'
        a.click()
      } else {
        window.location.href = href
      }
    }

    if (scrollToTop) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    onClick?.(e)
  }

  const resolvedVariant = variant ?? 'cta'
  const resolvedSize = size ?? (resolvedVariant === 'cta' || resolvedVariant === 'cta-dark' ? 'cta' : undefined)

  return (
    <Button
      ref={buttonRef}
      onClick={handleClick}
      variant={resolvedVariant}
      size={resolvedSize}
      className={cn(className)}
      type={type}
      {...props}
    >
      {children}
    </Button>
  )
}
